// ============================================================================
// QuantumAI Shield -- Admin Panel
// File: src/components/AdminPanel.jsx
// SECURITY: Email whitelist -- only ADMIN_EMAILS can access this component
// ============================================================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient.js';

const ADMIN_EMAILS = [
    'karan@quantumaishield.com',
    'karancxo@gmail.com',
];

// Dev: absolute URL forces fetch to hit vercel dev port 3000 directly
// Prod: relative URL -- same domain, Vercel routes /api/* to serverless functions
const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '';


function maskEmail(email) {
    if (!email) return '[unknown]';
    const parts = email.split('@');
    if (parts.length !== 2) return '[invalid]';
    return parts[0].slice(0, 2) + '***@' + parts[1];
}

function fmtDate(iso) {
    if (!iso) return '--';
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    });
}

function fmtDateTime(iso) {
    if (!iso) return '--';
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        timeZone: 'America/New_York',
    }) + ' ET';
}

const PAGE_LABELS = {
    welcome: 'Welcome', dashboard: 'Dashboard', risk: 'Risk Score',
    euclassifier: 'EU Classifier', checklist: 'Checklist', documents: 'Documents',
    mapper: 'Mapper', chatbot: 'AI Assistant', settings: 'Settings', admin: 'Admin',
};
function fmtPage(page) {
    if (!page) return '--';
    return PAGE_LABELS[page] || page;
}

function fmtDuration(seconds) {

    if (seconds === null || seconds === undefined) return '--';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
}

// QLedger status badge — three states:
//   Sealed   — user has completed at least one seal (qledger_sealed)
//   Eligible — QLedger purchased/granted but not yet used (qledger_eligible, not sealed)
//   Locked   — no QLedger access at all
function qledgerBadge(u) {
    if (u.qledger_sealed) {
        return { label: 'Sealed', bg: 'rgba(34,197,94,0.12)', color: '#4ade80' };
    }
    if (u.qledger_eligible) {
        return { label: 'Eligible', bg: 'rgba(0,204,255,0.12)', color: '#00ccff' };
    }
    return { label: 'Locked', bg: 'rgba(100,116,139,0.12)', color: '#64748b' };
}

export default function AdminPanel({ user, onNavigate }) {

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [selected, setSelected] = useState(null);
    const [qledgerReason, setQledgerReason] = useState('');
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityError, setActivityError] = useState(null);

    const isAdmin = !!(user && user.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));


    async function loadAll() {
        const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
        if (sessionError) {
            setError('Session refresh failed');
            setActivityError('Session refresh failed');
            return;
        }
        await Promise.all([loadUsers(session.access_token), loadActivity(session.access_token)]);
    }

    useEffect(() => {
        if (!isAdmin) return;
        loadAll();
    }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadActivity(accessToken) {
        setActivityLoading(true);
        setActivityError(null);
        try {
            const token = accessToken || (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) throw new Error('No active session');

            const res = await fetch(API_URL + '/api/admin-activity', {
                headers: {
                    'Authorization': 'Bearer ' + token,


                    'Content-Type': 'application/json',
                }
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);

            const data = await res.json();
            // FIX: this previously called setUsers(...) and read data.users --
            // a copy-paste leftover from loadUsers() that (a) left setActivity
            // permanently unused (the build-breaking ESLint error) and (b) could
            // silently overwrite the real Users table state with activity data
            // if this resolved after loadUsers() inside the Promise.all in loadAll().
            // CONFIRM: "activity" below must match whatever key server.js's
            // GET /api/admin/activity route actually returns -- update this one
            // key name if the backend uses a different field (e.g. data.rows).
            setActivity(data.activity || []);

        } catch (err) {
            console.error('[AdminPanel] loadActivity error:', err.message);
            setActivityError('Failed to load activity: ' + err.message);
        } finally {
            setActivityLoading(false);
        }
    }

    async function loadUsers(accessToken) {
        setLoading(true);
        setError(null);
        try {
            const token = accessToken || (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) throw new Error('No active session');

            const res = await fetch(API_URL + '/api/admin-users', {



                headers: {
                         'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json',
                }
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const data = await res.json();
            setUsers(data.users || []);
        } catch (err) {
            console.error('[AdminPanel] loadUsers error:', err.message);
            setError('Failed to load users: ' + err.message);
        } finally {
            setLoading(false);
        }
    }

    async function changePlan(userId, plan) {
        setMsg(null);
        try {

            // REPLACE WITH:
            const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
            if (sessionError) throw new Error('Session refresh failed');


            const res = await fetch(API_URL + '/api/admin-plan', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + session.access_token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plan, userId }),
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            setMsg({ ok: true, text: 'Plan updated to ' + plan });
            setSelected(null);
            loadUsers();
        } catch (err) {
            setMsg({ ok: false, text: 'Failed: ' + err.message });
        }
    }

    // Task #6: manual QLedger grant/revoke — requires a reason (audit trail),
    // mirrors changePlan()'s auth pattern (refreshSession → Bearer JWT →
    // backend does the real authorization check, this UI gate is cosmetic).
    async function setQledger(userId, qledgerEligible) {
        setMsg(null);
        if (!qledgerReason || !qledgerReason.trim()) {
            setMsg({ ok: false, text: 'A reason is required to change QLedger access.' });
            return;
        }
        try {
            const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
            if (sessionError) throw new Error('Session refresh failed');

            const res = await fetch(API_URL + '/api/admin-qledger', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + session.access_token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                    qledger_eligible: qledgerEligible,
                    reason: qledgerReason.trim(),
                }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || ('HTTP ' + res.status));
            }
            setMsg({ ok: true, text: 'QLedger access ' + (qledgerEligible ? 'granted' : 'revoked') });
            setQledgerReason('');
            setSelected(null);
            loadUsers();
        } catch (err) {
            setMsg({ ok: false, text: 'Failed: ' + err.message });
        }
    }

    // Security gate -- after all hooks
    if (!isAdmin) {
        return (
            <div style={{ padding: 40, color: 'white', background: '#0a0f1e', minHeight: '100vh' }}>
                <h2 style={{ color: '#ef4444' }}>Access Denied</h2>
                <p>You do not have permission to view this page.</p>
                <button
                    onClick={() => onNavigate && onNavigate('dashboard')}
                    style={{ background: '#1e40af', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
                >
                    Back to Dashboard
                </button>
            </div>
        );
    }

    const paid = users.filter(u => u.plan && u.plan !== 'trial').length;
    const mrr = users.filter(u => u.plan === 'starter').length * 199
              + users.filter(u => u.plan === 'professional').length * 349
              + users.filter(u => u.plan === 'enterprise').length * 1299;

    // QLedger counts for the stats row — same three states as qledgerBadge()
    const qledgerSealed   = users.filter(u => u.qledger_sealed).length;
    const qledgerEligible = users.filter(u => u.qledger_eligible && !u.qledger_sealed).length;
    const qledgerLocked   = users.length - qledgerSealed - qledgerEligible;

    return (
        <div style={{ minHeight: '100vh', background: '#0a0f1e', color: '#e2e8f0', padding: 32, fontFamily: 'system-ui, sans-serif' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div>
                    <h1 style={{ margin: 0, color: '#00ccff', fontSize: 24, fontWeight: 700 }}>Admin Panel</h1>
                    <p style={{ margin: '4px 0 0', color: '#475569', fontSize: 12 }}>Quantum AI Shield LLC -- Internal use only</p>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                                        <button onClick={loadAll} style={{ background: 'transparent', border: '1px solid #00ccff', color: '#00ccff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
                        Refresh
                    </button>

                    <button onClick={() => onNavigate && onNavigate('dashboard')} style={{ background: 'transparent', border: '1px solid #64748b', color: '#64748b', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
                        Back
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Users', value: users.length, color: '#00ccff' },
                    { label: 'Paid', value: paid, color: '#4ade80' },
                    { label: 'Trial', value: users.length - paid, color: '#60a5fa' },
                    { label: 'Est. MRR', value: '$' + mrr.toLocaleString(), color: '#fbbf24' },
                    { label: 'QLedger Sealed', value: qledgerSealed, color: '#4ade80' },
                    { label: 'QLedger Eligible', value: qledgerEligible, color: '#00ccff' },
                    { label: 'QLedger Locked', value: qledgerLocked, color: '#64748b' },
                ].map(s => (
                    <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 120 }}>
                        <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{s.label}</div>
                        <div style={{ color: s.color, fontSize: 28, fontWeight: 800 }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Message */}
            {msg && (
                <div style={{ background: msg.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', border: '1px solid ' + (msg.ok ? '#22c55e' : '#ef4444'), borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: msg.ok ? '#22c55e' : '#ef4444', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                    {msg.text}
                    <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>X</button>
                </div>
            )}

            {/* Users Table */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 24 }}>
                <div style={{ color: '#00ccff', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>All Users</div>

                {loading && <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>Loading users...</div>}
                {error && <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>{error}</div>}

                {!loading && !error && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                            <thead>
                                <tr>
                                    {['Email', 'Plan', 'Joined', 'Paid Until', 'API Calls', 'QLedger', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'center', color: '#00ccff', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 && (
                                    <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#475569' }}>No users found</td></tr>
                                )}
                                {users.map(u => {
                                    const badge = qledgerBadge(u);
                                    return (
                                    <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0' }}>{maskEmail(u.email)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <span style={{ background: '#1e3a5f', color: '#60a5fa', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                                                {u.plan || 'trial'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{fmtDate(u.created_at)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: u.paid_until && new Date(u.paid_until) > new Date() ? '#4ade80' : '#ef4444', fontSize: 12 }}>
                                            {fmtDate(u.paid_until)}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0' }}>
                                            {u.ai_calls_used || 0} / {u.ai_calls_limit === 999999 ? 'Unlimited' : (u.ai_calls_limit || '--')}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <span style={{ background: badge.bg, color: badge.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                                                {badge.label}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => { setSelected(selected && selected.id === u.id ? null : u); setQledgerReason(''); }}
                                                style={{ background: 'transparent', border: '1px solid #00ccff', color: '#00ccff', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
                                            >
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Login Activity Table */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 24, marginTop: 24 }}>
                <div style={{ color: '#00ccff', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Login Activity</div>

                {activityLoading && <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>Loading activity...</div>}
                {activityError && <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>{activityError}</div>}

                {!activityLoading && !activityError && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 1 }}>
                            <thead>
                                <tr>
                                    {['Email', 'IP Address', 'Browser / OS', 'Device', 'Current Screen', 'Login Time', 'Last Seen', 'Duration', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'center', color: '#00ccff', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {activity.length === 0 && (
                                    <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#475569' }}>No login activity yet</td></tr>
                                )}
                                {activity.map(a => (
                                    <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0' }}>{maskEmail(a.email)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' }}>{a.ip_address || '--'}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontSize: 12 }}>{a.browser} / {a.os}</td>

                                        <td style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{a.device_type}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#22d3ee', fontSize: 12, fontWeight: 600 }}>{fmtPage(a.current_page)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{fmtDateTime(a.login_at)}</td>

                                        <td style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{fmtDateTime(a.last_seen_at)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontSize: 12 }}>{fmtDuration(a.session_duration_seconds)}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            {a.is_online ? (
                                                <span style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>🟢 Online</span>
                                            ) : a.logout_at ? (
                                                <span style={{ background: 'rgba(100,116,139,0.12)', color: '#64748b', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>Logged out</span>
                                            ) : (
                                                <span style={{ background: 'rgba(100,116,139,0.12)', color: '#64748b', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>Idle</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Plan Change Panel */}
            {selected && (


                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 24, marginTop: 24 }}>
                    <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
                        Managing: {maskEmail(selected.email)}
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: 13 }}>Change plan to:</span>
                        {['trial', 'starter', 'professional', 'enterprise', 'qledger'].map(plan => (
                            <button
                                key={plan}
                                onClick={() => changePlan(selected.id, plan)}
                                style={{
                                    background: (selected.plan || 'trial') === plan ? 'rgba(0,204,255,0.1)' : 'transparent',
                                    border: '1px solid #00ccff',
                                    color: '#00ccff',
                                    borderRadius: 6,
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {plan}
                            </button>
                        ))}
                        <button
                            onClick={() => setSelected(null)}
                            style={{ background: 'transparent', border: '1px solid #64748b', color: '#64748b', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>

                    {/* Task #6: QLedger grant/revoke control */}
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ color: '#94a3b8', fontSize: 13 }}>QLedger access:</span>
                            <span style={{
                                background: qledgerBadge(selected).bg,
                                color: qledgerBadge(selected).color,
                                borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            }}>
                                {qledgerBadge(selected).label}
                            </span>
                        </div>
                        <input
                            type="text"
                            value={qledgerReason}
                            onChange={(e) => setQledgerReason(e.target.value)}
                            placeholder="Reason for QLedger change (required, e.g. 'comped for beta feedback')"
                            style={{
                                width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#e2e8f0',
                                padding: '8px 10px', fontSize: 12, marginBottom: 10,
                            }}
                        />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button
                                onClick={() => setQledger(selected.id, true)}
                                disabled={!!selected.qledger_eligible}
                                style={{
                                    background: 'transparent', border: '1px solid #4ade80', color: '#4ade80',
                                    borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                    cursor: selected.qledger_eligible ? 'not-allowed' : 'pointer',
                                    opacity: selected.qledger_eligible ? 0.4 : 1,
                                }}
                            >
                                Grant QLedger
                            </button>
                            <button
                                onClick={() => setQledger(selected.id, false)}
                                disabled={!selected.qledger_eligible}
                                style={{
                                    background: 'transparent', border: '1px solid #ef4444', color: '#ef4444',
                                    borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                    cursor: !selected.qledger_eligible ? 'not-allowed' : 'pointer',
                                    opacity: !selected.qledger_eligible ? 0.4 : 1,
                                }}
                            >
                                Revoke QLedger
                            </button>
                        </div>
                    </div>

                    <div style={{ marginTop: 12, padding: 12, background: 'rgba(255,255,255,0.02)', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
                        <strong style={{ color: '#94a3b8' }}>User ID:</strong> {selected.id}<br/>
                        <strong style={{ color: '#94a3b8' }}>Current plan:</strong> {selected.plan || 'trial'}<br/>
                        <strong style={{ color: '#94a3b8' }}>API access:</strong> {selected.api_access_enabled ? 'Enabled' : 'Disabled'}<br/>
                        <strong style={{ color: '#94a3b8' }}>QLedger sealed:</strong> {selected.qledger_sealed ? ('Yes -- ' + fmtDate(selected.qledger_sealed_at)) : 'No'}<br/>
                        {selected.qledger_grant_reason && (
                            <>
                                <strong style={{ color: '#94a3b8' }}>Last grant reason:</strong> {selected.qledger_grant_reason}<br/>
                                <strong style={{ color: '#94a3b8' }}>Last grant at:</strong> {fmtDate(selected.qledger_grant_at)}<br/>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}